/**
 * Valores semânticos para largura de coluna.
 *
 * Representam intenções de layout, abstraindo valores CSS concretos.
 *
 * @remarks
 * - FILL: ocupa o espaço disponível (1fr)
 * - CONTENT: ajusta ao conteúdo (max-content)
 */
export const COLUMN_WIDTH_SEMANTICS = {
  FILL: 'fill',
  CONTENT: 'content',
} as const

/**
 * Mapeia larguras semânticas para valores CSS reais.
 */
export const COLUMN_WIDTH_MAP: Record<(typeof COLUMN_WIDTH_SEMANTICS)[keyof typeof COLUMN_WIDTH_SEMANTICS], string> = {
  [COLUMN_WIDTH_SEMANTICS.FILL]: '1fr',
  [COLUMN_WIDTH_SEMANTICS.CONTENT]: 'max-content',
}

/**
 * Densidades disponíveis para controle de espaçamento da tabela.
 *
 * @remarks
 * - SMALL: compacto
 * - MEDIUM: padrão
 * - LARGE: espaçado
 */
export const TABLE_DENSITY = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
} as const

/**
 * Mapeia densidades para valores CSS de espaçamento.
 */
export const TABLE_DENSITY_MAP: Record<(typeof TABLE_DENSITY)[keyof typeof TABLE_DENSITY], string> = {
  [TABLE_DENSITY.SMALL]: '8px',
  [TABLE_DENSITY.MEDIUM]: '16px',
  [TABLE_DENSITY.LARGE]: '24px',
}

/**
 * Opções de alinhamento do conteúdo nas células.
 *
 * @remarks
 * - START
 * - CENTER
 * - END
 */
export const TABLE_ALIGNMENT = {
  START: 'start',
  CENTER: 'center',
  END: 'end',
} as const

/**
 * Mapeia alinhamentos para valores CSS de posicionamento.
 */
export const TABLE_ALIGNMENT_MAP: Record<(typeof TABLE_ALIGNMENT)[keyof typeof TABLE_ALIGNMENT], string> = {
  [TABLE_ALIGNMENT.START]: 'flex-start',
  [TABLE_ALIGNMENT.CENTER]: 'center',
  [TABLE_ALIGNMENT.END]: 'flex-end',
}

/**
 * Estilos visuais disponíveis para divisores.
 *
 * @remarks
 * - SOLID
 * - DASHED
 */
export const TABLE_DIVIDER_STYLE = {
  SOLID: 'solid',
  DASHED: 'dashed',
} as const

/**
 * Mapeia estilos de divisor para valores CSS reais.
 */
export const TABLE_DIVIDER_STYLE_MAP: Record<(typeof TABLE_DIVIDER_STYLE)[keyof typeof TABLE_DIVIDER_STYLE], string> = {
  [TABLE_DIVIDER_STYLE.SOLID]: 'solid',
  [TABLE_DIVIDER_STYLE.DASHED]: 'dashed',
}

/**
 * Opções de comportamento de overflow para células.
 *
 * @remarks
 * - HIDDEN: oculta o conteúdo que ultrapassa os limites da célula
 * - VISIBLE: permite que o conteúdo ultrapasse os limites da célula
 */
export const TABLE_CELL_OVERFLOW = {
  HIDDEN: 'hidden',
  VISIBLE: 'visible',
} as const

/**
 * Opções de comportamento de overflow para texto dentro das células.
 *
 * @remarks
 * - ELLIPSIS: exibe reticências (...) quando o texto ultrapassa os limites da célula
 * - CLIP: corta o texto que ultrapassa os limites da célula
 */
export const TABLE_CELL_TEXT_OVERFLOW = {
  ELLIPSIS: 'ellipsis',
  CLIP: 'clip',
} as const

/**
 * Opções de controle de quebra de linha para células.
 *
 * @remarks
 * - NOWRAP: impede que o texto quebre em múltiplas linhas
 * - NORMAL: permite que o texto quebre normalmente
 */
export const TABLE_CELL_WHITE_SPACE = {
  NOWRAP: 'nowrap',
  NORMAL: 'normal',
} as const

/**
 * Opções de controle de truncagem para células.
 *
 * @remarks
 * - TRUNCATE: exibe reticências (...) quando o texto ultrapassa os limites da célula
 * - WRAP: permite que o texto quebre em múltiplas linhas
 */
export const TABLE_OVERFLOW = {
  TRUNCATE: 'truncate',
  WRAP: 'wrap',
} as const

/**
 * Estados semânticos disponíveis para o tooltip das células.
 *
 * @remarks
 * - INFO
 * - WARNING
 * - DANGER
 * - SUCCESS
 */
export const TABLE_TOOLTIP_STATE = {
  INFO: 'info',
  WARNING: 'warning',
  DANGER: 'danger',
  SUCCESS: 'success',
} as const

/**
 * Modos de resolução do tooltip nas células.
 *
 * @remarks
 * - INHERIT: continua a resolução hierárquica
 * - ENABLED: habilita o tooltip explicitamente
 * - DISABLED: desabilita o tooltip explicitamente
 */
export const TABLE_TOOLTIP_MODE = {
  INHERIT: 'inherit',
  ENABLED: 'enabled',
  DISABLED: 'disabled',
} as const

/**
 * Mapeia opções de controle de truncagem para configurações CSS correspondentes.
 */
export const TABLE_OVERFLOW_MAP = {
  [TABLE_OVERFLOW.TRUNCATE]: {
    overflow: TABLE_CELL_OVERFLOW.HIDDEN,
    textOverflow: TABLE_CELL_TEXT_OVERFLOW.ELLIPSIS,
    whiteSpace: TABLE_CELL_WHITE_SPACE.NOWRAP,
  },
  [TABLE_OVERFLOW.WRAP]: {
    overflow: TABLE_CELL_OVERFLOW.VISIBLE,
    textOverflow: TABLE_CELL_TEXT_OVERFLOW.CLIP,
    whiteSpace: TABLE_CELL_WHITE_SPACE.NORMAL,
  },
} as const
